import type { QueueState } from './model.ts';

/**
 * What a press means when a queue is in the room.
 *
 * Two controls put a video into a queue and they are pressed for different reasons, so they decide
 * differently — but both decide *here*, because "is there a queue, and is something playing" is a
 * question about state rather than about a button, and a decision table inside a click handler is a
 * decision table nobody tests.
 */

/**
 * What the **Add to queue** button on a card does.
 *
 * All three arms put the video at the **end**, and the button never asks. It is one press with one
 * meaning, which is what a control repeated forty times to a page has to be: the card already shows
 * which of its two faces it is wearing, and the press that puts a video somewhere other than the end
 * is a press on the video itself, where the reader is choosing rather than collecting.
 */
export type QueueAddAction =
  /**
   * There is no queue and nothing playing: this video becomes the queue. `play` is the Playback
   * tab's "Start playing on arrival" — a *request*, since the browser has the last word on it
   * (`settings/model.ts`) — and the corner player appears either way, because a queue the reader
   * cannot see is a queue they cannot press play on.
   */
  | { readonly kind: 'start'; readonly play: boolean }
  /**
   * There is no queue but a **different** video is playing, on the watch page or in the corner. The
   * queue becomes those two videos in that order, so the list starts with what the reader is already
   * watching rather than interrupting it. Nothing about the playback changes and no navigation
   * happens: the press said "after this", not "instead of this".
   */
  | { readonly kind: 'startWithCurrent' }
  /**
   * There is no queue, and the video being added is the one **already playing** — the reader pressed
   * Add to queue on the video they are watching, or on the one in the corner player.
   *
   * The queue becomes that single video, and the playback is left exactly as it is: it is on screen
   * already, so there is nothing to start and restarting it would reload the manifest and drop the
   * picture back to the beginning. Its own arm rather than a shape of `startWithCurrent`, because
   * that one builds a list of two and the two would be the same video — which is how a press on the
   * card you are watching used to leave the queue holding it twice.
   */
  | { readonly kind: 'adoptCurrent' }
  /** There is already a queue, so the video goes on the end of it. */
  | { readonly kind: 'append' };

export function decideQueueAdd(input: {
  readonly queue: QueueState;
  /** The video this press is adding. */
  readonly videoId: string;
  /**
   * What the watch page or the corner player is on, and `null` when nothing is playing. The id
   * rather than a flag: "is this the video already on screen" is the question the two starting arms
   * are told apart by, and a boolean cannot answer it.
   */
  readonly playingVideoId: string | null;
  /** The Playback tab's "Start playing on arrival". */
  readonly autoplay: boolean;
}): QueueAddAction {
  if (input.queue.entries.length > 0) return { kind: 'append' };
  if (input.playingVideoId === null) return { kind: 'start', play: input.autoplay };
  if (input.playingVideoId === input.videoId) return { kind: 'adoptCurrent' };
  return { kind: 'startWithCurrent' };
}

/**
 * What a plain press on a **video itself** does — a card's picture or its title, anywhere in the app.
 *
 * Without a queue it is what it has always been: a link to the video, followed by the browser. With
 * one it asks, because the reader has a list running and opening a video is now two different acts —
 * play it now and lose the list, or play it next and keep it — and the difference between them is a
 * list the reader spent time building.
 *
 * **Only a plain press.** A middle-click, ⌘/Ctrl-click, Shift-click or Alt-click is the browser's own
 * vocabulary for "open this somewhere else", and taking one over to ask a question would break an
 * affordance every other link in the app honours. Those are decided by {@link isPlainPress} and never
 * reach here.
 */
export type QueueCardPress = 'follow' | 'ask';

export function decideCardPress(queue: QueueState): QueueCardPress {
  return queue.entries.length > 0 ? 'ask' : 'follow';
}

/** The modifiers a press carries, as a plain record — the DOM stays outside (R13). */
export interface PressModifiers {
  /** `0` is the primary button. Anything else is the browser's, middle-click included. */
  readonly button: number;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

/**
 * Whether this press is the app's to answer.
 *
 * Everything else belongs to the browser: ⌘/Ctrl opens a new tab, Shift a new window, Alt downloads
 * on some platforms, and the middle button is a new tab everywhere. Cancelling any of them to ask a
 * question would take a habit away from the reader and give them nothing they asked for.
 */
export function isPlainPress(event: PressModifiers): boolean {
  return (
    event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey
  );
}
